<template>
  <div class="phone-input" :class="{'phone-input--number-wide': numberWide}">
    <div class="phone-input__country">
      <button
          type="button"
          class="phone-input__code"
          aria-haspopup="listbox"
          :aria-expanded="countryMenuOpen"
          @click="countryMenuOpen = !countryMenuOpen"
          @keydown.esc="countryMenuOpen = false"
      >
        <img
            v-if="selectedCountryOption"
            class="phone-input__flag"
            :src="flagUrl(selectedCountryOption.countryCode)"
            alt=""
            width="24"
            height="18"
        >
        <span class="phone-input__selected-text">
          {{ selectedCountryOption ? selectedCountryOption.countryName : 'Select country' }}
        </span>
        <span v-if="selectedCountryOption" class="phone-input__dial-code">{{ selectedCountryOption.dialCode }}</span>
        <span class="phone-input__chevron" aria-hidden="true">&#9662;</span>
      </button>
      <div v-if="countryMenuOpen" class="phone-input__menu" role="listbox">
        <button
            v-for="option in countryOptions"
            :key="option.countryCode"
            type="button"
            class="phone-input__option"
            :class="{'phone-input__option--selected': option.countryCode === selectedCountry}"
            role="option"
            :aria-selected="option.countryCode === selectedCountry"
            @click="selectCountry(option.countryCode)"
        >
          <img :src="flagUrl(option.countryCode)" alt="" width="24" height="18">
          <span class="phone-input__option-name">{{ option.countryName }}</span>
          <span class="phone-input__dial-code">{{ option.dialCode }}</span>
        </button>
      </div>
    </div>
    <input
        v-model="nationalNumber"
        class="phone-input__number"
        type="tel"
        inputmode="tel"
        autocomplete="tel-national"
        placeholder="Phone number"
        required
        @input="emitValue"
    >
  </div>
</template>

<script>
import countries from '@/data/countries.json';
import {
  COUNTRY_DIAL_CODES,
  dialCodeForCountry,
  formatInternationalPhone,
  internationalEntry,
  PRIMARY_COUNTRY_FOR_DIAL_CODE,
  splitInternationalPhone,
} from '@/utils/phone';

export default {
  name: 'PhoneInput',
  props: {
    modelValue: {type: String, default: ''},
    residence: {type: String, default: ''},
    numberWide: {type: Boolean, default: false},
  },
  emits: ['update:modelValue'],
  data() {
    return {
      selectedCountry: '',
      nationalNumber: '',
      syncing: false,
      lastEmittedValue: null,
      userPickedCountry: false,
      countryMenuOpen: false,
    };
  },
  computed: {
    countryOptions() {
      return countries
          .filter((country) => COUNTRY_DIAL_CODES[country.code])
          .map((country) => ({
            countryCode: country.code,
            countryName: country.name,
            dialCode: COUNTRY_DIAL_CODES[country.code],
          }));
    },
    dialCode() {
      return dialCodeForCountry(this.selectedCountry);
    },
    selectedCountryOption() {
      return this.countryOptions.find((option) => option.countryCode === this.selectedCountry);
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        const isOwnUpdate = this.lastEmittedValue !== null && value === this.lastEmittedValue;
        this.lastEmittedValue = null;
        // Our own emit echoing back: keep the user's text (and caret) as typed.
        if (isOwnUpdate) return;
        const parsed = splitInternationalPhone(value, this.residence);
        this.syncing = true;
        this.selectedCountry = this.resolveCountry(parsed.dialCode);
        this.nationalNumber = parsed.nationalNumber;
        this.$nextTick(() => {
          this.syncing = false;
        });
      },
    },
    residence(value) {
      if (this.userPickedCountry || String(this.modelValue || '').startsWith('+')) return;
      const countryCode = String(value || '').toUpperCase();
      this.selectedCountry = COUNTRY_DIAL_CODES[countryCode] ? countryCode : '';
      if (this.nationalNumber) this.emitValue();
    },
  },
  mounted() {
    document.addEventListener('click', this.closeCountryMenuOnOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeCountryMenuOnOutsideClick);
  },
  methods: {
    flagUrl(countryCode) {
      return `https://flagcdn.com/24x18/${String(countryCode || '').toLowerCase()}.png`;
    },
    // Prefer the current pick, then residence, then the only country using
    // that code, then that shared code's primary country (+1 → US).
    resolveCountry(dialCode) {
      const selectedCode = String(this.selectedCountry || '').toUpperCase();
      const residenceCode = String(this.residence || '').toUpperCase();
      if (COUNTRY_DIAL_CODES[selectedCode] === dialCode) return selectedCode;
      if (COUNTRY_DIAL_CODES[residenceCode] === dialCode) return residenceCode;
      const matches = this.countryOptions.filter((option) => option.dialCode === dialCode);
      if (matches.length === 1) return matches[0].countryCode;
      const primary = PRIMARY_COUNTRY_FOR_DIAL_CODE[dialCode];
      return matches.some((option) => option.countryCode === primary) ? primary : '';
    },
    selectCountry(countryCode) {
      this.userPickedCountry = true;
      this.selectedCountry = countryCode;
      this.countryMenuOpen = false;
      this.emitValue();
    },
    closeCountryMenuOnOutsideClick(event) {
      if (!this.$el.contains(event.target)) this.countryMenuOpen = false;
    },
    emitValue() {
      if (this.syncing) return;
      // A pasted "+82 10…" carries its own country code: switch the country
      // and keep only the national part so the code isn't prefixed twice.
      const entry = internationalEntry(this.nationalNumber);
      if (entry) {
        const parsed = splitInternationalPhone(entry, this.residence);
        this.selectedCountry = this.resolveCountry(parsed.dialCode);
        this.nationalNumber = parsed.nationalNumber;
      }
      const value = formatInternationalPhone(this.dialCode, this.nationalNumber);
      // Vue won't fire the watcher for an unchanged value, so don't leave a
      // marker behind that could swallow a later external update.
      this.lastEmittedValue = value === this.modelValue ? null : value;
      this.$emit('update:modelValue', value);
    },
    commitValue() {
      if (!this.selectedCountry || !this.nationalNumber) return this.modelValue;
      const value = formatInternationalPhone(this.dialCode, this.nationalNumber);
      this.lastEmittedValue = value;
      this.$emit('update:modelValue', value);
      return value;
    },
    hasSelectedCountry() {
      return Boolean(this.selectedCountry);
    },
  },
};
</script>

<style scoped lang="scss">
.phone-input {
  display: grid;
  grid-template-columns: minmax(12rem, 58%) minmax(0, 1fr);

  &__code,
  &__number {
    background: #fff;
    border: 1px solid #E2E3EC;
    color: #23253A;
    font: inherit;
    height: 42px;
    min-width: 0;
    padding: .55rem .7rem;
  }

  &__country {
    min-width: 0;
    position: relative;
  }

  &__flag {
    border-radius: 2px;
    flex: 0 0 auto;
    object-fit: cover;
    pointer-events: none;
  }

  &__code {
    align-items: center;
    border-radius: 12px 0 0 12px;
    cursor: pointer;
    display: flex;
    gap: .55rem;
    text-align: left;
    width: 100%;
  }

  &__selected-text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__dial-code {
    color: #3B46B8;
    flex: 0 0 auto;
    font-size: .88em;
    font-weight: 700;
  }

  &__chevron {
    flex: 0 0 auto;
  }

  &__menu {
    background: #fff;
    border: 1px solid #E2E3EC;
    border-radius: 12px;
    box-shadow: 0 12px 30px rgba(31, 44, 92, .18);
    left: 0;
    max-height: 280px;
    max-width: min(24rem, calc(100vw - 2rem));
    min-width: 100%;
    overflow-y: auto;
    position: absolute;
    top: calc(100% + 4px);
    width: max-content;
    z-index: 20;
  }

  &__option {
    align-items: center;
    background: #fff;
    border: 0;
    color: #23253A;
    cursor: pointer;
    display: flex;
    font: inherit;
    gap: .65rem;
    padding: .65rem .75rem;
    text-align: left;
    width: 100%;

    &:hover,
    &:focus,
    &--selected {
      background: #F4F4F8;
      outline: none;
    }

    img {
      border-radius: 2px;
      flex: 0 0 auto;
      object-fit: cover;
    }
  }

  &__option-name {
    flex: 1 1 auto;
  }

  &__number {
    border-left: 0;
    border-radius: 0 10px 10px 0;
  }

  &__code:focus,
  &__number:focus {
    border-color: #1f2c5c;
    outline: none;
    position: relative;
    z-index: 1;
  }
}

.phone-input--number-wide {
  grid-template-columns: minmax(12rem, 38%) minmax(0, 1fr);
}

@media only screen and (max-width: 480px) {
  .phone-input {
    grid-template-columns: 1fr;

    &__code {
      border-radius: 10px 10px 0 0;
    }

    &__number {
      border-left: 1px solid #E2E3EC;
      border-radius: 0 0 10px 10px;
      border-top: 0;
    }
  }
}
</style>
